import * as React from 'react';
import { UniversalLoadingState } from './UniversalLoadingState';
import { UniversalEmptyState } from './UniversalEmptyState';
import { NotificationCard } from '../product/NotificationCard';
import { Button } from '../primitives/Button';

/**
 * UniversalAsyncBoundary — the four states of any async panel, in one place.
 *
 * Why this exists (ADR_SHARED_COMPONENT_DOCTRINE_2026-08-03, item S5):
 * loading / empty / error / populated was being re-implemented per page, and the
 * error arm was routinely the one omitted. That produced two real defects:
 *
 *   - AdminDashboard's cron strip sat on "Loading cron health…" forever because
 *     its fetch was wrapped in a bare `.catch(() => {})`.
 *   - SalesDashboard rendered "0 open leads · R0 commission" on a failed load —
 *     visually identical to a genuinely empty pipeline, on a commission screen.
 *
 * Both are the same bug: an unhandled state rendering as a confident fact.
 *
 * PRECEDENCE (deliberate, do not reorder):
 *   error → loading → empty → children
 *
 * `error` wins over `loading` so a retry in flight keeps the failure visible
 * rather than flickering back to a skeleton. `isLoading` should reflect the
 * FIRST load only; on refetch keep rendering stale data (stale-while-revalidate)
 * rather than collapsing the panel.
 *
 * Composes UniversalLoadingState / UniversalEmptyState / NotificationCard — it
 * deliberately renders no state markup of its own beyond the retry affordance.
 */

export interface UniversalAsyncBoundaryProps {
  /** True during the FIRST load only. On refetch, keep showing stale data. */
  isLoading: boolean;
  /** Error message. Non-null wins over every other state. */
  error?: string | null;
  /** Fetch succeeded but there is nothing to render. */
  isEmpty?: boolean;

  /** Skeleton shape. Match it to the real layout so nothing reflows. */
  loadingVariant?: 'rows' | 'cards' | 'spinner';
  loadingCount?: number;
  /** Caption for the spinner variant. */
  loadingCaption?: string;

  emptyTitle?: string;
  emptyDescription?: string;
  emptyIcon?: React.ComponentType<{ className?: string }>;
  emptyAction?: React.ReactNode;
  /** Compact empty state — for in-card panels rather than whole pages. */
  emptyCompact?: boolean;

  /** Headline for the error state. The detail comes from `error`. */
  errorTitle?: string;
  /** Renders a Retry button on the error state when supplied. */
  onRetry?: () => void;

  children: React.ReactNode;
}

export function UniversalAsyncBoundary({
  isLoading,
  error = null,
  isEmpty = false,
  loadingVariant = 'rows',
  loadingCount = 3,
  loadingCaption,
  emptyTitle = 'Nothing to show',
  emptyDescription,
  emptyIcon,
  emptyAction,
  emptyCompact,
  errorTitle = 'Could not load this section',
  onRetry,
  children,
}: UniversalAsyncBoundaryProps) {
  if (error) {
    return (
      <NotificationCard
        tone="danger"
        title={errorTitle}
        body={
          onRetry ? (
            <span className="flex flex-col items-start gap-2">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={onRetry}>
                Try again
              </Button>
            </span>
          ) : (
            error
          )
        }
      />
    );
  }

  if (isLoading) {
    return (
      <UniversalLoadingState
        variant={loadingVariant}
        count={loadingCount}
        caption={loadingCaption}
      />
    );
  }

  if (isEmpty) {
    return (
      <UniversalEmptyState
        icon={emptyIcon}
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
        compact={emptyCompact}
      />
    );
  }

  return <>{children}</>;
}
